import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Upload, RefreshCw, CheckCircle, AlertCircle, Globe, Database, FileText } from 'lucide-react';
import GLBViewer from '../components/GLBViewer';
import { DataTable, Column } from '../components/DataTable';
import Papa from 'papaparse';
import './Rotate.css';
import ModelMultiSelect from '../components/ModelMultiSelect';

export type ParsedPoint = Record<string, any>;
export type Model = { label: string; value: string };

const MODELS: Model[] = [
  { label: 'ALL',                   value: 'ALL' },
  { label: 'MERDITH2021',           value: 'MERDITH2021' },
  { label: 'TorsvikCocks2017',      value: 'TorsvikCocks2017' },
  { label: 'MATTHEWS2016_pmag_ref', value: 'MATTHEWS2016_pmag_ref' },
  { label: 'GOLONKA',               value: 'GOLONKA' },
  { label: 'PALEOMAP',              value: 'PALEOMAP' },
];

// Inline styles para selects — gana a Tailwind Preflight sin !important
const SELECT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '2px solid #e0e7ff',
  borderRadius: '10px',
  fontSize: '0.85rem',
  fontWeight: 500,
  color: '#1e293b',
  background: 'white',
  cursor: 'pointer',
  boxSizing: 'border-box',
  outline: 'none',
  appearance: 'none',
  WebkitAppearance: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
};

const Rotate: React.FC = () => {
  const [file,               setFile]               = useState<File | null>(null);
  const [originalPoints,     setOriginalPoints]     = useState<ParsedPoint[]>([]);
  const [rotatedPoints,      setRotatedPoints]      = useState<ParsedPoint[]>([]);
  const [filteredPoints,     setFilteredPoints]     = useState<ParsedPoint[]>([]);
  const [originalHeaders,    setOriginalHeaders]    = useState<string[]>([]);
  const [loading,            setLoading]            = useState(false);
  const [error,              setError]              = useState<string | null>(null);
  const [periods,            setPeriods]            = useState<string[]>([]);
  const [selectedPeriod,     setSelectedPeriod]     = useState('PaleoAtlas');
  const [modelUrl,           setModelUrl]           = useState('PaleoAtlas.glb');
  const [selectedModels,     setSelectedModels]     = useState<string[]>(['ALL']);
  const [search,             setSearch]             = useState('');
  const [fileLoaded,         setFileLoaded]         = useState(false);
  const [progress,           setProgress]           = useState(0);
  const [completed,          setCompleted]          = useState(false);
  const [dataLoaded,         setDataLoaded]         = useState(false);
  const [isResetting,        setIsResetting]        = useState(false);
  const [availableCsvModels, setAvailableCsvModels] = useState<string[]>([]);
  const [selectedCsvModel,   setSelectedCsvModel]   = useState('');
  const [periodDisplayMap, setPeriodDisplayMap] = useState<Record<string, string>>({
    PaleoAtlas: 'Present-day (Holocene)',
  });  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const intervalRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const smoothRef    = useRef<ReturnType<typeof setInterval> | null>(null);

  
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (smoothRef.current)   clearInterval(smoothRef.current);
    };
  }, []);

  const safeToFixed = (val: any): string => {
    if (val == null) return '-';
    const num = Number(val);
    if (isNaN(num)) return String(val);
    const [, dec = ''] = num.toString().split('.');
    return dec.length > 2 ? num.toFixed(2).replace(/\.?0+$/, '') : String(val);
  };

  const buildColumns = (keys: string[]): Column<ParsedPoint>[] =>
    keys.map(key => ({ header: key, accessor: key, cell: (v: any) => safeToFixed(v) }));

  const originalColumns = useMemo(() => buildColumns(originalHeaders), [originalHeaders]);
  const rotatedColumns = useMemo(() => {
    if (!rotatedPoints.length) return [];
    return buildColumns(Object.keys(rotatedPoints[0]).filter(k => k !== 'display_name')).map(col => {
      if (col.accessor === 'period') {
        return {
          ...col,
          cell: (v: any) => periodDisplayMap[v] ?? v,
        };
      }
      return col;
    });
  }, [rotatedPoints, periodDisplayMap]);
  
  
  useEffect(() => {
    let pts = rotatedPoints;
    if (search) {
      const s = search.toLowerCase();
      pts = pts.filter(p => Object.values(p).some(v => String(v).toLowerCase().includes(s)));
    }
    setFilteredPoints(pts);
  }, [rotatedPoints, search]);
  

  useEffect(() => {
    if (progress >= 100 && dataLoaded) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (smoothRef.current)   clearInterval(smoothRef.current);
      setCompleted(true);
      setLoading(false);
    }
  }, [progress, dataLoaded]);

  const handleReset = () => {
    setIsResetting(true);
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (smoothRef.current)   clearInterval(smoothRef.current);
    setTimeout(() => {
      setFile(null);
      setFileLoaded(false);
      setOriginalPoints([]);
      setOriginalHeaders([]);
      setRotatedPoints([]);
      setFilteredPoints([]);
      setSelectedModels(['ALL']);
      setLoading(false);
      setCompleted(false);
      setProgress(0);
      setError(null);
      setPeriods([]);
      setSelectedPeriod('PaleoAtlas');
      setModelUrl('PaleoAtlas.glb');
      setSelectedCsvModel('');
      setAvailableCsvModels([]);
      setDataLoaded(false);
      setSearch('');
      setPeriodDisplayMap({ PaleoAtlas: 'Present-day (Holocene)' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTimeout(() => setIsResetting(false), 300);
    }, 300);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    Papa.parse<ParsedPoint>(f, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data, meta }) => {
        setOriginalHeaders(meta.fields ?? []);
        setOriginalPoints(data as ParsedPoint[]);
        setFile(f);
        setFileLoaded(true);
        setError(null);
      },
      error: (err) => setError('Error parsing CSV: ' + err.message),
    });
  };

  const handleModelChange = (values: string[]) => {
    setSelectedModels(values.includes('ALL') ? ['ALL'] : values.filter(v => v !== 'ALL'));
  };

  const parseRotatedCSV = (text: string) => {
    Papa.parse<ParsedPoint>(text, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }: Papa.ParseResult<ParsedPoint>) => {
        try {
          const numOrNull = (x: any) => { const n = parseFloat(x); return isFinite(n) ? n : null; };

          const rotated: ParsedPoint[] = (data as ParsedPoint[]).map(raw => {
            const period = String(raw.period ?? raw.Period ?? '').trim();
            const model  = String(raw.model  ?? raw.Model  ?? '').trim();
            const clean: any = {};
            for (const [k, v] of Object.entries(raw)) {
              if (/^periods?$/i.test(k) || /^models?$/i.test(k)) continue;
              clean[k] = v;
            }
            clean.lat    = numOrNull(raw.lat);
            clean.lng    = numOrNull(raw.lng);
            clean.p_lat  = numOrNull(raw.p_lat);
            clean.p_lng  = numOrNull(raw.p_lng);
            clean.period = period || null;
            clean.model  = model  || null;
            return clean as ParsedPoint;
          });

          if (!rotated.length) {
            setError('No valid rotated points with period/model found in CSV.');
            setLoading(false);
            return;
          }

          setRotatedPoints(rotated);

          const uniquePeriods = Array.from(new Set(rotated.map(p => p.period).filter(Boolean)));
            setPeriods(uniquePeriods);
            if (uniquePeriods.length) {
              setSelectedPeriod(uniquePeriods[0]);
              setModelUrl(`mapas_3D/${uniquePeriods[0]}.glb`);
            }

            const displayMap: Record<string, string> = {};
            rotated.forEach(p => {
              if (p.period && p.display_name) {
                displayMap[p.period] = p.display_name;
              }
            });
            setPeriodDisplayMap(displayMap);

          const uniqueModels = Array.from(new Set(rotated.map(p => p.model).filter(Boolean)));
          setAvailableCsvModels(uniqueModels);
          setSelectedCsvModel(uniqueModels[0] ?? '');

          setLoading(false);
        } catch (err) {
          setError('Error processing rotated CSV: ' + String(err));
          setLoading(false);
        }
      },
      error: (err: any) => {
        setError('Error parsing rotated CSV: ' + err.message);
        setLoading(false);
      },
    });
  };

  const handleFileSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!file) { setError('Please select a CSV file first.'); return; }

    setLoading(true);
    setCompleted(false);
    setProgress(0);
    setError(null);
    setRotatedPoints([]);
    setFilteredPoints([]);
    setSelectedPeriod('');
    setPeriods([]);
    setDataLoaded(false);

    const kb = file.size / 1024;
    const [delay, step] =
      kb < 1   ? [80,  1.5] :
      kb < 100 ? [100, 1.0] :
      kb < 500 ? [120, 0.5] :
                 [150, 0.3];

    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) { clearInterval(intervalRef.current!); return prev; }
        return Math.min(prev + step, 90);
      });
    }, delay);

    try {
      const form = new FormData();
      form.append('file', file);
      form.append('model', selectedModels.includes('ALL') ? 'ALL' : selectedModels.join(','));

      const res = await fetch(`${import.meta.env.BASE_URL}api/rotate-file`, {
        method: 'POST',
        body: form,
      });

      if (!res.ok) {
        let msg = `Error ${res.status}: Unable to process file`;
        try {
          const d = await res.json();
          const t = typeof d.error === 'string' ? d.error : '';
          if      (d.message)                            msg = d.message;
          else if (t.includes('debe contener columnas')) msg = '📋 Invalid CSV format. Required columns: lat, lng, age_min, age_max';
          else if (t.includes('cannot open file'))       msg = '📄 Could not read the CSV file. Check format and encoding.';
          else if (t.includes('Execution halted'))       msg = '⚠️ Processing stopped. Verify columns: lat, lng, age_min, age_max';
        } catch {
          const t = await res.text().catch(() => '');
          if (t.includes('debe contener columnas')) msg = '📋 Invalid CSV format. Required columns: lat, lng, age_min, age_max';
        }
        throw new Error(msg);
      }

      if (intervalRef.current) clearInterval(intervalRef.current);

      const csvText = await res.text();
      parseRotatedCSV(csvText);

      if (smoothRef.current) clearInterval(smoothRef.current);
      smoothRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) { clearInterval(smoothRef.current!); return 100; }
          return prev + 2;
        });
      }, 40);

      setDataLoaded(true);
    } catch (err: any) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (smoothRef.current)   clearInterval(smoothRef.current);
      setLoading(false);
      setProgress(0);
      setError(err.message ?? 'Error uploading file. Please try again.');
    }
  };

  const downloadCSV = (data: ParsedPoint[], filename: string) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const escape  = (v: any) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv  = [headers.join(','), ...data.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = Object.assign(document.createElement('a'), { href: url, download: filename });
    link.click();
    URL.revokeObjectURL(url);
  };

  const hasResults = originalPoints.length > 0 && rotatedPoints.length > 0;

  return (
    <>
      <section className="globe-section">
        <div className="globe-viewer">
        <GLBViewer
        modelUrl={modelUrl}
        originalPoints={originalPoints}
        rotatedPoints={filteredPoints.filter(p => !selectedCsvModel || p.model === selectedCsvModel)}
        selectedPeriod={selectedPeriod}
        selectedPeriodLabel={periodDisplayMap[selectedPeriod] ?? (selectedPeriod === 'PaleoAtlas' ? 'Present-day (Holocene)' : selectedPeriod)}
        exportEnabled={true}
      />
        </div>
      </section>

      <aside className="sidebar">
        <div className="sidebar-header">
          <Globe className="sidebar-icon" />
          <h2 className="sidebar-title">Rotate</h2>
        </div>

        <form
          onSubmit={handleFileSubmit}
          className={`sidebar-form ${isResetting ? 'resetting' : ''}`}
        >
          <div className="form-group">
            <label htmlFor="csvfile" className="form-label">
              <Upload className="label-icon" /> CSV File
            </label>
            <div className="file-input-wrapper">
              <input
                ref={fileInputRef} id="csvfile" type="file"
                accept=".csv,text/csv" onChange={handleFileChange}
                className="file-input-hidden" required
              />
              <label htmlFor="csvfile" className={`file-input-label ${fileLoaded ? 'file-loaded' : ''}`}>
                <span className="file-input-text">{fileLoaded ? '✓ Loaded' : 'Select File'}</span>
                <span className="file-input-icon">
                  {fileLoaded
                    ? <CheckCircle className="icon-success" />
                    : <Database className="icon-upload" />}
                </span>
              </label>
              {file && <span className="file-name">{file.name}</span>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Model</label>
            <ModelMultiSelect
              models={MODELS}
              value={selectedModels}
              onChange={handleModelChange}
            />
          </div>

          <div className="button-group">
            <button
              type="submit" disabled={loading}
              className={`btn-submit ${loading ? 'loading' : ''} ${completed ? 'completed' : ''}`}
            >
              {loading && <span className="btn-progress" style={{ width: `${progress}%` }} />}
              <span className="btn-content">
                {loading ? (
                  <><RefreshCw className="btn-icon spinning" />{Math.round(progress)}%</>
                ) : completed ? (
                  <><CheckCircle className="btn-icon" />Done</>
                ) : (
                  <><Globe className="btn-icon" />Rotate</>
                )}
              </span>
            </button>
            <button type="button" onClick={handleReset} className="btn-reset" title="Reset form">
              <RefreshCw className="reset-icon" />
            </button>
          </div>

          {error && (
            <div className="error-box">
              <AlertCircle className="error-icon" />
              <span>{error}</span>
            </div>
          )}

          {periods.length > 0 && (
            <div className="form-group">
              <label htmlFor="period-select" className="form-label">Period</label>
              <select
                id="period-select"
                value={selectedPeriod}
                onChange={e => {
                  setSelectedPeriod(e.target.value);
                  setModelUrl(`mapas_3D/${e.target.value}.glb`);
                }}
                style={SELECT_STYLE}
              >
                {periods.map(p => (
                  <option key={p} value={p}>
                    {periodDisplayMap[p] ?? p}  {/* ← nombre visual */}
                  </option>
                ))}
              </select>

            </div>
          )}

          {availableCsvModels.length > 1 && (
            <div className="form-group">
              <label htmlFor="csv-model-select" className="form-label">Data Model</label>
              <select
                id="csv-model-select" value={selectedCsvModel}
                onChange={e => setSelectedCsvModel(e.target.value)}
                style={SELECT_STYLE}
              >
                {availableCsvModels.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}
        </form>
      </aside>

      {!hasResults ? (
        <section className="tables-section">
          <div className="table-card">
            <div className="table-card-header">
              <h3 className="table-card-title">PACA tutorial</h3>
              <p className="table-card-description">
                How to upload, rotate, explore, and export your paleocoordinates.
              </p>
            </div>
            <div className="tutorial">
              <h4>First: upload your file</h4>
              <p>Click "Select File" and choose the CSV you want to analyze. Required columns:</p>
              <ul>
                <li><code>name</code> – Unique identifier (e.g., Sample_1).</li>
                <li><code>lng</code> – Longitude in WGS84 decimal degrees (e.g., −3.688344).</li>
                <li><code>lat</code> – Latitude in WGS84 decimal degrees (e.g., 40.453054).</li>
                <li><code>age_min</code> – Minimum age in Ma (e.g., 137.05).</li>
                <li><code>age_max</code> – Maximum age in Ma (e.g., 143.1).</li>
              </ul>
              <p>After uploading, your points will appear on a 3D globe of the present-day Earth.</p>

              <h4>Choosing a rotation model</h4>
              <p>Use the "Model" menu to select a rotation model. If unsure, choose <strong>PALEOMAP</strong> (recommended). Click "Rotate" to start reconstruction.</p>

              <h4>Understanding the results</h4>
              <p>
                When finished, a results table will appear. Typical columns include <code>age_mean</code>,{' '}
                <code>p_lng</code>, <code>p_lat</code>, <code>period</code>, and <code>model</code>.
              </p>
              <p>The 3D globe will update to show reconstructed points on the paleogeographic map.</p>

              <h4>Map and display controls</h4>
              <ul>
                <li><strong>Data Model</strong>: choose which model's points are shown (only visible if you ran ALL).</li>
                <li><strong>Period</strong>: switch the paleogeographic map and filter points by geological period.</li>
              </ul>

              <h4>Exporting results</h4>
              <ul>
                <li>Export 3D model: downloads the current 3D view as a .glb with the visible points.</li>
                <li>Export dataset: downloads the reconstructed results table as CSV.</li>
              </ul>

              <div className="tutorial-downloads">
                <h4>Download resources</h4>
                <p>Get started quickly with the template file and the full user guide:</p>
                <div className="download-buttons">
                  <a href={`${import.meta.env.BASE_URL}template.csv`} download="template.csv"
                    className="download-btn download-btn--csv">
                    <Database className="btn-icon" /> template.csv
                  </a>
                  <a href={`${import.meta.env.BASE_URL}user_guide.pdf`} download="user_guide.pdf"
                    className="download-btn download-btn--pdf">
                    <FileText className="btn-icon" /> user_guide.pdf
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="tables-section">
          <div className="tables-container">
            <div className="table-card">
              <div className="table-card-header">
                <h3 className="table-card-title">Original Coordinates</h3>
                <p className="table-card-description">Input data from your CSV file</p>
              </div>
              <DataTable data={originalPoints} columns={originalColumns} search="" title="" />
            </div>

            <div className="table-card">
              <div className="table-card-header">
                <h3 className="table-card-title">Paleocoordinates</h3>
                <p className="table-card-description">Transformed ancient positions</p>
              </div>
              <div className="search-wrapper">
                <input type="text" placeholder="Search rotated points..."
                  value={search} onChange={e => setSearch(e.target.value)}
                  className="search-input" />
              </div>
              <DataTable
                data={filteredPoints}
                columns={rotatedColumns}
                search={search}
                onDownload={data => {
                  const ts = new Date().toISOString().replace(/[:.-]/g, '_');
                  const mapped = data.map(p => ({
                    ...p,
                    period: periodDisplayMap[p.period] ?? p.period,
                  }));
                  downloadCSV(mapped, `paleocoordinates_${ts}.csv`);
                }}
                
                title=""
              />
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Rotate;
